/**
 * Local Pagination Helper
 * Handles pagination on the client-side for data received from API
 */

export interface PaginationParams {
  currentPage: number;
  pageSize: number;
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
}

/**
 * Paginates array data locally
 * @param data - Full array of data from API
 * @param currentPage - Current page (0-indexed)
 * @param pageSize - Number of items per page
 * @returns Paginated data with total count and pages
 */
export const paginateData = <T>(
  data: T[],
  currentPage: number,
  pageSize: number
): PaginatedResult<T> => {
  const totalCount = data.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Validate page number
  const validPage = Math.max(0, Math.min(currentPage, totalPages - 1));

  const startIndex = validPage * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedData = data.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    totalCount,
    totalPages,
  };
};

/**
 * Validates if page number is within valid range
 * @param page - Page number to validate
 * @param totalPages - Total number of pages
 * @returns Valid page number
 */
export const validatePageNumber = (
  page: number,
  totalPages: number
): number => {
  return Math.max(0, Math.min(page, Math.max(0, totalPages - 1)));
};
